import React from 'react';
import { Smartphone, Lock, Bell, FileText, Download, ShieldCheck, Star } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { FeatureGrid } from '../components/FeatureGrid';
import { CTASection } from '../components/CTASection';
interface AppPageProps {
  onNavigate: (page: string) => void;
}
export function AppPage({
  onNavigate
}: AppPageProps) {
  const appFeatures = [{
    title: 'Alle Verträge im Blick',
    description: 'Zentrale Übersicht aller Versicherungen und Finanzen. Intuitiv und übersichtlich.',
    icon: FileText
  }, {
    title: 'Smarte Benachrichtigungen',
    description: 'Keine Kündigungsfrist mehr verpassen. Wir erinnern Sie proaktiv.',
    icon: Bell
  }, {
    title: 'Bank-Level Sicherheit',
    description: 'Ihre Daten sind durch modernste AES-256 Verschlüsselung geschützt.',
    icon: Lock
  }];
  return <div className="min-h-screen bg-white" data-id="element-278">
      {/* App Hero */}
      <section className="pt-24 pb-32 bg-slate-900 text-white overflow-hidden relative" data-id="element-279">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900" data-id="element-280"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-281">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-282">
            <div className="space-y-10 animate-in fade-in slide-in-from-left-8 duration-700" data-id="element-283">
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 px-4 py-1.5 text-sm font-medium text-blue-400 border border-blue-500/20" data-id="element-284">
                <Star className="h-4 w-4 fill-blue-400" data-id="element-285" />
                <span data-id="element-286">App des Jahres 2024</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight" data-id="element-287">
                Die{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400" data-id="element-288">
                  SmartProtect24
                </span>{' '}
                App
              </h1>
              <p className="text-xl text-slate-300 max-w-lg leading-relaxed" data-id="element-289">
                Ihr digitaler Versicherungsmanager für die Hosentasche.
                Verwalten, optimieren und sparen – jederzeit und überall mit
                maximaler Sicherheit.
              </p>
              <div className="flex flex-col sm:flex-row gap-5" data-id="element-290">
                <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 h-14 px-8 text-lg font-semibold" data-id="element-291">
                  <Download className="mr-2 h-5 w-5" data-id="element-292" /> iOS Download
                </Button>
                <Button size="lg" variant="outline" className="text-white border-slate-600 hover:bg-slate-800 hover:text-white h-14 px-8 text-lg" data-id="element-293">
                  <Download className="mr-2 h-5 w-5" data-id="element-294" /> Android Download
                </Button>
              </div>
              <div className="pt-6 flex items-center gap-6 text-sm text-slate-400 border-t border-slate-800 w-fit" data-id="element-295">
                <div className="flex gap-1" data-id="element-296">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="h-5 w-5 text-amber-400 fill-amber-400" data-id="element-297" />)}
                </div>
                <span className="font-medium text-white" data-id="element-298">
                  4.9/5 im App Store
                </span>
              </div>
            </div>
            <div className="relative animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200" data-id="element-299">
              <div className="absolute inset-0 bg-blue-500/30 blur-[100px] rounded-full" data-id="element-300"></div>
              {/* Phone Mockup Placeholder */}
              <div className="mx-auto w-[320px] h-[650px] bg-slate-950 rounded-[3.5rem] border-[8px] border-slate-800 shadow-2xl flex items-center justify-center overflow-hidden relative z-10 ring-1 ring-white/10" data-id="element-301">
                <div className="w-full h-full bg-slate-900 flex flex-col relative" data-id="element-302">
                  {/* Top Bar */}
                  <div className="absolute top-0 w-full h-8 bg-black/50 z-20 flex justify-center" data-id="element-303">
                    <div className="w-32 h-6 bg-black rounded-b-2xl" data-id="element-304"></div>
                  </div>
                  {/* Screen Content */}
                  <div className="h-full w-full bg-gradient-to-b from-slate-800 to-slate-900 flex flex-col items-center justify-center text-slate-500 p-6" data-id="element-305">
                    <ShieldCheck className="h-16 w-16 text-blue-500 mb-4" data-id="element-306" />
                    <h3 className="text-white font-bold text-xl mb-2" data-id="element-307">
                      SmartProtect24
                    </h3>
                    <p className="text-center text-sm" data-id="element-308">Ihr Schutz ist aktiv.</p>

                    {/* Mock UI Elements */}
                    <div className="w-full mt-8 space-y-3" data-id="element-309">
                      <div className="h-16 bg-slate-800/50 rounded-xl border border-slate-700 w-full" data-id="element-310"></div>
                      <div className="h-16 bg-slate-800/50 rounded-xl border border-slate-700 w-full" data-id="element-311"></div>
                      <div className="h-16 bg-slate-800/50 rounded-xl border border-slate-700 w-full" data-id="element-312"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FeatureGrid title="Funktionen, die begeistern" subtitle="Entwickelt für Menschen, die Wert auf Qualität und Zeitersparnis legen." features={appFeatures} data-id="element-313" />

      {/* Security Section */}
      <section className="py-24 bg-slate-50 relative overflow-hidden" data-id="element-314">
        <div className="container mx-auto px-4 md:px-6 text-center relative z-10" data-id="element-315">
          <div className="inline-flex items-center justify-center p-5 bg-green-100 text-green-600 rounded-full mb-8 shadow-sm" data-id="element-316">
            <Lock className="h-10 w-10" data-id="element-317" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-318">
            Sicherheit steht an erster Stelle
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-16 leading-relaxed" data-id="element-319">
            Wir nutzen die gleichen Sicherheitsstandards wie führende Banken.
            Ihre Daten werden ausschließlich verschlüsselt übertragen und auf
            zertifizierten deutschen Servern gespeichert.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-320">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:-translate-y-1 transition-transform" data-id="element-321">
              <ShieldCheck className="h-10 w-10 text-blue-600 mb-4 mx-auto" data-id="element-322" />
              <h3 className="font-bold text-xl mb-3 text-slate-900" data-id="element-323">
                SSL-Verschlüsselung
              </h3>
              <p className="text-slate-500 leading-relaxed" data-id="element-324">
                256-Bit AES Hochsicherheits-Verschlüsselung für alle
                Datenübertragungen.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:-translate-y-1 transition-transform" data-id="element-325">
              <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-slate-700" data-id="element-326">
                DE
              </div>
              <h3 className="font-bold text-xl mb-3 text-slate-900" data-id="element-327">
                Deutsche Server
              </h3>
              <p className="text-slate-500 leading-relaxed" data-id="element-328">
                Hosting ausschließlich in ISO 27001 zertifizierten Rechenzentren
                in Frankfurt.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100 hover:-translate-y-1 transition-transform" data-id="element-329">
              <Lock className="h-10 w-10 text-green-600 mb-4 mx-auto" data-id="element-330" />
              <h3 className="font-bold text-xl mb-3 text-slate-900" data-id="element-331">
                Datenschutz
              </h3>
              <p className="text-slate-500 leading-relaxed" data-id="element-332">
                Volle DSGVO-Konformität. Ihre Daten gehören Ihnen – kein Verkauf
                an Dritte.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTASection title="Holen Sie sich die Kontrolle zurück" description="Laden Sie die SmartProtect24 App jetzt herunter und starten Sie in wenigen Minuten." primaryCtaText="Jetzt kostenlos starten" onPrimaryClick={() => console.log('Download')} data-id="element-333" />
    </div>;
}